import { useEffect, useRef, useState } from "react";
import { ReactComponent as UploadIcon } from "../../assets/icons/form/upload-icon.svg";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { RedButton } from "../buttons/RedButton";
import { LOG } from "../../utils/logUtil";
import axios from "axios";
import { Loading } from "../indicators/Loading";
import { SecondaryButton } from "../buttons/SecondaryButton";

export const AbsoluteUpload = () => {
  /** Refs */
  const fileInputRef = useRef(null);
  /** State */
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [modal, setShowModal] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleUploadClick = () => {
    if (fileInputRef?.current) {
      // @ts-ignore
      fileInputRef?.current?.click();
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePath(reader.result as string);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const uploadImage = async () => {
    setIsLoading(true);
    try {
      const body = new FormData();
      body.append("file", image as File);
      body.append("type", "dashboard");
      const response = await axios.post(
        "https://clean-cycle-dev.ew.r.appspot.com/storage/upload?=",
        body,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (response?.data?.filePath) {
        setUploadedImage(response?.data?.filePath);
      }
    } catch (error) {
      LOG.error("Error on uploading image", { error });
    } finally {
      setIsLoading(false);
    }
  };

  const clipboardCopy = () => {
    navigator.clipboard.writeText(uploadedImage ?? "");
    setIsCopied(true);
  };

  useEffect(() => {
    if (!modal) {
      setImage(null);
      setUploadedImage(null);
      setIsLoading(false);
      setImagePath(null);
    }
  }, [modal]);

  return (
    <>
      <div
        onClick={() => setShowModal(true)}
        className="fixed bottom-5 right-5 cursor-pointer rounded-full justify-center items-center hover:opacity-50 transition ease-linear"
      >
        <UploadIcon className="w-[50px] h-[50px]" />
      </div>
      {/* Add Balance Modal */}
      {modal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/3 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Upload and get image link
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <div
                    onClick={handleUploadClick}
                    className={`w-full py-[29px] flex flex-col justify-center items-center border border-dashed border-primary-base rounded bg-sky-lighter cursor-pointer`}
                  >
                    {imagePath ? (
                      <img
                        src={imagePath}
                        alt="uploaded"
                        className={`w-full h-[200px] sm:h-[400px] object-contain`}
                      />
                    ) : (
                      <>
                        <UploadIcon className="mb-4" />
                        <div className="flex flex-row items-center">
                          <p className="font-satoshi text-sm font-medium text-primary-base">
                            Click to upload
                          </p>
                          <p className="font-satoshi text-sm font-medium text-gray-text ml-1">
                            or drag and drop
                          </p>
                        </div>
                        <p className="font-satoshi text-sm font-medium text-gray-text">
                          SVG, PNG, JPG or GIF
                        </p>
                        <p className="font-satoshi text-sm font-medium text-gray-text">
                          (max, 800 X 800px)
                        </p>
                      </>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept=".svg, .png, .jpg, .gif"
                      onChange={onChange}
                    />
                  </div>
                  <PrimaryButton
                    disabled={!image}
                    onClick={uploadImage}
                    label="Get the link"
                    className="mt-3 w-full"
                  />
                  <div className="flex flex-row items-center w-full">
                    <input
                      disabled
                      value={uploadedImage ?? ""}
                      placeholder="Image link..."
                      className={`w-full mt-3 h-[46px] px-4 ${
                        isCopied ? "bg-green-100" : "bg-sky-lighter"
                      } border border-gray-border focus:border-focused-border rounded font-satoshi text-base text-dark font-medium outline-none`}
                    />
                    <SecondaryButton
                      disabled={!uploadedImage}
                      onClick={clipboardCopy}
                      className="mt-3 h-[46px] ml-1"
                      label={isCopied ? "Copied" : "Copy"}
                    />
                  </div>
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={() => setShowModal(false)}
                  />
                </div>
              </div>
            </div>
            {isLoading ? <Loading /> : null}
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </>
  );
};
